import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getOptions, searchPrograms } from '../api.js';
import { useDebouncedValue } from './useDebouncedValue.js';
import { useDismissingNote } from './useDismissingNote.js';
import { pruneInvalidSelections, programFields, formatCleanupMessage } from '../filterDependency.js';

/**
 * The whole Search Programs state machine.
 *
 * One reducer for the filters, not a dozen useStates, for a specific reason:
 * nearly every control has to reset the page and discard the accumulated
 * results when it changes, and "Load more" must NOT. Spreading that rule across
 * separate setters is how a filter change silently keeps page 3's rows on
 * screen.
 */

/** The request body, minus paging. Everything here is a filter the user set. */
export const EMPTY_FILTERS = {
  q: '',
  countries: [],
  // Student Nationality — the reference's "Source Country". An ELIGIBILITY
  // filter, not an access one: the server ANDs it onto the caller's scope, so
  // it can only narrow. Empty means "no nationality named", which adds no
  // clause at all rather than defaulting to one.
  nationality: '',
  universities: [],
  // The reference's own course-type vocabulary — filters on the NAME
  // (`coursesWebsite.courseLevel`: "Foundation", "Undergraduate (Top-up)",
  // "Masters", ...), sent as `degreeLevels`. NOT the numeric option-1 id: the
  // id->name mapping is not 1:1 in the source data (id 3 covers "Associate
  // Degree", "Undergraduate" and "Diploma" across different rows), so filtering
  // by id answers a different question than the reference asks. See
  // buildFilterClauses in server.js.
  degreeLevels: [],
  // Exact intakes, chosen by id. "January/February 2027" is a different intake
  // from "March/April 2027" and both are different from a coarse "Jan 2027".
  intakeIds: [],
  // Active status: '' (off) or 'active' (only courses running an intake the
  // admin console has switched on). Mutually exclusive with `intakeIds` in the
  // UI — see the reducer's SET_ACTIVE_STATUS and the intakeIds branch of
  // TOGGLE below. The wire value is unchanged from before ('' / 'active'); only
  // the UI now refuses to hold both at once. The Inactive value this control
  // used to offer is gone; only "" and "active" are reachable from the UI now.
  intakeStatus: '',
  tuitionMin: null,
  tuitionMax: null,
  // The reference matches an option-20 duration NAME against the raw
  // `coursesWebsite.duration` string as a case-insensitive substring — "12
  // months" and "1 Year" are two different filters, not a month range. Holds
  // the chosen labels; OR semantics, same TOGGLE action as englishTests.
  durationLabels: [],
  englishTests: [],
  scholarship: false,
  freeToApply: false,
  // Opt-in: intake and English are both strict unless the user widens them.
  includeUnknownIntake: false,
  includeUnknownEnglish: false,
};

export const INITIAL = {
  filters: EMPTY_FILTERS,
  sort: 'relevance',
  page: 1,
  // Set only by "Load more". Every other change resets it.
  appending: false,
};

const MAX_REBUILD_PAGES = 5;

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Append a page, dropping anything already on screen.
 *
 * The server sorts on a total order now (see SORT_TIEBREAKER in server.js), so
 * a course cannot change places with a tied neighbour between two "Load more"
 * clicks. This guards the case a tiebreaker cannot reach: a course *inserted or
 * deleted* mid-walk shifts every row after it by one, which can push a row the
 * user has already seen onto the next page.
 *
 * Rendering the same course twice is the visible half of that; a row silently
 * skipped is the other half and cannot be fixed from here - it needs the server
 * to page from a cursor rather than an offset. Dropping the duplicate is
 * strictly better than showing it, and honest about the count either way,
 * because "Load more" reports `hits.length`, not `page * perPage`.
 */
function appendUnseen(previous, incoming) {
  const seen = new Set(previous.map((h) => h.id));
  return previous.concat(incoming.filter((h) => !seen.has(h.id)));
}

export function reducer(state, action) {
  // Any filter or sort change starts a new result set. Only LOAD_MORE opts out.
  const fresh = (patch) => ({ ...state, ...patch, page: 1, appending: false });
  const withFilters = (patch) => fresh({ filters: { ...state.filters, ...patch } });

  switch (action.type) {
    case 'SET_QUERY':
      return withFilters({ q: action.value });

    // The header control is single-select, but the API field is a list.
    case 'SET_COUNTRY':
      return withFilters({ countries: action.value ? [action.value] : [] });

    case 'SET_NATIONALITY':
      return withFilters({ nationality: action.value });

    case 'TOGGLE': {
      const key = action.key === 'durations' ? 'durationLabels' : action.key;
      const next = toggleIn(state.filters[key] ?? [], action.value);
      // "Active status" and "Course intake (exact)" are mutually exclusive.
      // The picker is normally disabled while Active status is on (see
      // SearchBar), but picking an id here always turns Active status off too
      // — belt and braces, so the two can never end up selected together no
      // matter which control drove the change.
      if (action.key === 'intakeIds' && next.length > state.filters.intakeIds.length) {
        return withFilters({ intakeIds: next, intakeStatus: '' });
      }
      return withFilters({ [key]: next });
    }

    case 'SET_FLAG':
      return withFilters({ [action.key]: action.value });

    // The "Active status" toggle. Turning it ON clears any exact-intake
    // selection (the two controls are mutually exclusive — see SearchBar);
    // turning it OFF just drops the clause, leaving intakeIds untouched (it is
    // already empty, since the picker is disabled whenever this is on).
    case 'SET_ACTIVE_STATUS':
      return withFilters(
        action.value ? { intakeStatus: 'active', intakeIds: [] } : { intakeStatus: '' },
      );

    case 'SET_TUITION':
      return withFilters({ tuitionMin: action.min, tuitionMax: action.max });

    /**
     * A dependent filter is dropped because the search just run for it came
     * back saying it is no longer reachable — never because a parent control
     * refused to change. The parent change that triggered this already
     * committed on its own dispatch; this is always a second, follow-up
     * dispatch (see the `run()` completion in useProgramSearch below), one per
     * response, carrying every field that needs to change at once so the
     * cleanup itself costs exactly one more search, not one per field.
     *
     * `action.patch` holds only the fields that actually changed — a field the
     * caller found nothing invalid in is simply absent, so this can never
     * clear something valid it was never told about.
     */
    case 'PRUNE_INVALID':
      return withFilters(action.patch);

    case 'SET_SORT':
      return fresh({ sort: action.value });

    case 'CLEAR_ALL':
      return INITIAL;

    case 'LOAD_MORE':
      return { ...state, page: state.page + 1, appending: true };

    default:
      return state;
  }
}

export function useProgramSearch() {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [hits, setHits] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only the keyword is debounced here. The tuition range is debounced too, but
  // in its own control: it is two fields a user tabs between, and 200ms fires a
  // search in the gap.
  const debouncedQ = useDebouncedValue(state.filters.q, 200);

  /**
   * The exact body sent to the API.
   *
   * `q` comes from the debounced value, not from `filters.q`: the input stays
   * responsive to every keystroke while the request waits.
   *
   * Deliberately absent: tenantId. Access scope is the server's decision and a
   * body value for it is ignored. `nationality` IS sent — it is the Student
   * Nationality control, and the server only ever lets it narrow.
   */
  // `intakeStatus` and `intakeIds` are sent exactly as the reducer holds them.
  // The UI keeps them mutually exclusive (see SET_ACTIVE_STATUS and the
  // intakeIds branch of TOGGLE above), so in practice at most one of the two
  // is ever non-empty; the server (buildFilterClauses) still knows how to
  // honour both at once if a future caller ever sends them together.
  const body = useMemo(() => {
    const { durationLabels, durations, ...restFilters } = state.filters;
    const resolvedDurations = durations ?? durationLabels ?? [];
    return {
      ...restFilters,
      durations: resolvedDurations,
      courseTypes: state.filters.degreeLevels,
      degreeLevels: state.filters.degreeLevels,
      intakeIds: state.filters.intakeIds,
      q: debouncedQ,
      sort: state.sort,
      page: state.page,
      // 24 per page — divides evenly into the 2/3/4-column card grid, and "Load
      // more" then advances the count 24 at a time.
      perPage: 24,
    };
  }, [state.filters, state.sort, state.page, debouncedQ]);

  /**
   * A signature of everything that changes the RESULT SET (not the page). While
   * this differs from the signature that produced the current `data`, that data
   * is for a previous query — `stale` below, which the result bar reads so it
   * shows "…" instead of flashing the old catalogue total under the new filter.
   * Paging ("Load more") is excluded on purpose: it never changes `found`.
   */
  const bodyKey = useMemo(
    () => JSON.stringify({ ...state.filters, q: debouncedQ, sort: state.sort }),
    [state.filters, debouncedQ, state.sort],
  );
  const [resultsKey, setResultsKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getOptions()
      .then((opts) => { if (!cancelled) setOptions(opts); })
      .catch(() => {
        if (cancelled) return;
        setError("We couldn't load the program search. Please refresh the page.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  /**
   * A monotonic request id, so a slow response cannot overwrite a faster one
   * issued after it. Every filter change fires a request, and they do not come
   * back in order.
   */
  const requestId = useRef(0);

  // `run` is a stable `useCallback([])`, so anything it reads that can change
  // across renders comes in through a ref rather than a closure — `options`
  // for the cleanup check below, same reason `appendingRef` already exists.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [cleanupNote, announceCleanup] = useDismissingNote();

  const run = useCallback(async (searchBody, { append, key, filters }) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);

    try {
      const result = await searchPrograms(searchBody);
      if (requestId.current !== id) return;
      setData(result);
      setError(null);
      setHits((prev) => (append ? appendUnseen(prev, result.hits) : result.hits));
      // The result bar can now trust `data.found` again — see `bodyKey`.
      if (key !== undefined) setResultsKey(key);

      /**
       * Invalid-filter cleanup — never on "Load more" (`append`), which reruns
       * the SAME filters a page deeper and cannot have invalidated anything.
       *
       * This response's `facets` already carry, for every field with an active
       * filter, counts measured with THAT field's own clause lifted (see
       * server.js's `countSearches`) — exactly "is this still valid given
       * every OTHER current filter", for whatever just changed to produce this
       * response. Acting on it here, as a follow-up dispatch rather than
       * inside SET_COUNTRY/SET_NATIONALITY themselves, is what keeps the
       * parent change itself instant and unconditional: it always commits on
       * its own dispatch, and cleanup is a second, later step once the answer
       * is known — never a reason to block or delay the first one.
       */
      if (!append && filters) {
        const { patch, removed } = pruneInvalidSelections(filters, result.facets, programFields(optionsRef.current));
        if (removed.length > 0) {
          dispatch({ type: 'PRUNE_INVALID', patch });
          announceCleanup(formatCleanupMessage(removed));
        }
      }
    } catch (err) {
      if (requestId.current !== id) return;
      setError(err.message);
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [announceCleanup]);

  // `appending` is read through a ref so it does not itself trigger the effect;
  // the reducer already bumped `page`, which is what the effect watches.
  const appendingRef = useRef(false);
  appendingRef.current = state.appending;

  useEffect(() => {
    if (!options) return;
    run(body, { append: appendingRef.current, key: bodyKey, filters: state.filters });
  }, [body, options, run, bodyKey, state.filters]);

  /**
   * Re-run the current search because the index moved.
   *
   * Pages are rebuilt in order rather than reset to page one, because "Load
   * more" accumulates and resetting would throw away what the user paged
   * through. Past MAX_REBUILD_PAGES it declines and returns false, so the
   * caller can offer the refresh instead of spending six round trips.
   *
   * Options are deliberately NOT reloaded: they repaint the whole rail from
   * catalogue-wide lists, and courses were measured changing 6 times in 30 days,
   * so the risk of disturbing a live selection outweighs the freshness.
   * Per-search facet counts do update, through the responses below.
   */
  const refreshFromIndex = useCallback(async () => {
    const pages = state.page;
    if (pages > MAX_REBUILD_PAGES) return false;

    const y = window.scrollY;
    for (let p = 1; p <= pages; p += 1) {
      // Sequential on purpose: each page appends to the one before it.
      // eslint-disable-next-line no-await-in-loop
      await run({ ...body, page: p }, { append: p > 1, key: bodyKey });
    }
    window.scrollTo({ top: y });
    return true;
  }, [body, bodyKey, run, state.page]);

  return {
    filters: state.filters,
    sort: state.sort,
    page: state.page,
    options,
    data,
    hits,
    error,
    loading,
    // `data` is for a previous set of filters — the result bar shows "…" rather
    // than the stale count while the new search is in flight.
    stale: bodyKey !== resultsKey,
    dispatch,
    refreshFromIndex,
    // Non-blocking notice: "Course Type 'X' was removed because it isn't
    // available with your other filters." Self-clears — see useDismissingNote.
    cleanupNote,
  };
}
